import { put, call, takeEvery, takeLatest, getContext, select } from 'redux-saga/effects';
import { message } from 'antd';
import { selectAppDetails } from '@organisation/redux/selectors';
import { doCreateUbiquityV2Client } from '@redux/doCreateUbiquityClient';
import i18n from '../../i18n';
import {
  loadContentMessages,
  contentMessagesLoaded,
  ICreateContentMessage,
  IUpdateContentMessage,
  createContentMessageSuccess,
  createContentMessageFailed,
  updateContentMessageSuccess,
  updateContentMessageFailed,
  deleteContentMessagesSuccess,
  deleteContentMessagesFailed,
  IDeleteContentMessages,
  publishContentMessageFailed,
  publishContentMessageSuccess,
  unpublishContentMessageFailed,
  unpublishContentMessageSuccess,
  IPublishContentMessage,
  IUnpublishContentMessage,
} from './actions';
import { IContentMessage } from './types';
import takeFirst from '../takeFirst';
import { mapRawContentMessageToContentMessage } from './utils';
import { trackDataEvent } from '../../analytics';
import { selectContentMessage } from './selectors';

export default function* root() {
  yield takeFirst('content-messages/fetch', doFetchContentMessages);
  yield takeLatest('content-messages/create', doCreateContentMessage);
  yield takeLatest('content-messages/update', doUpdateContentMessage);
  yield takeLatest('content-messages/delete', doDeleteContentMessages);
  yield takeEvery('content-messages/publish', doPublishContentMessage);
  yield takeEvery('content-messages/unpublish', doUnpublishContentMessage);
}

function* doFetchContentMessages(): any {
  try {
    yield put(loadContentMessages());
    const { appToken } = yield select(selectAppDetails);
    const client = (yield call(doCreateUbiquityV2Client)).messages(appToken);
    const pagedClient = client.pagination().begin(client.list());

    let results: IContentMessage[] = [];

    while (pagedClient.hasNext()) {
      const nextData = yield call(pagedClient.next);

      results = [...results, ...nextData.map(mapRawContentMessageToContentMessage)];
    }

    yield put(contentMessagesLoaded(results));
  } catch (err) {
    console.error(err);
    yield call(message.warning, 'Failed to load messages');
  }
}

function* doCreateContentMessage({
  payload: { message: newMessage, type, messageTranslationKey, path },
}: ICreateContentMessage): any {
  try {
    const { appToken } = yield select(selectAppDetails);
    const client = (yield call(doCreateUbiquityV2Client)).messages(appToken);
    const contentMessage = yield call(client.create, { name: newMessage.name });
    const version = yield call(client.createVersion, contentMessage, {
      body: newMessage.body,
      metadata: newMessage.metadata,
      payload: newMessage.payload,
      title: newMessage.name,
    });

    yield call(client.packageVersion, version, contentMessage);

    if (newMessage.published) {
      yield call(client.makeAvailable, contentMessage);
    }

    yield call(
      message.success,
      i18n.t(`${messageTranslationKey}:NewContentMessageWizard.successMessage`),
    );

    const createdMessage = mapRawContentMessageToContentMessage({
      ...contentMessage,
      latest_version: version,
      is_available: newMessage.published || false,
    });

    trackDataEvent('Content Message Created', {
      messageId: createdMessage.id,
      messageTitle: createdMessage.name,
      messageType: type,
    });

    yield put(createContentMessageSuccess(createdMessage));

    const history = yield getContext('history');
    const newPath = path.replace('new', contentMessage.uuid);

    yield call(history.replace, newPath);
  } catch (err) {
    console.error('create error', err);
    yield call(
      message.warning,
      i18n.t(`${messageTranslationKey}:NewContentMessageWizard.failedMessage`),
    );
    yield put(createContentMessageFailed());
  }
}

function* doUpdateContentMessage({
  payload: { message: updatedMessage, type, messageTranslationKey, id },
}: IUpdateContentMessage): any {
  try {
    const { appToken } = yield select(selectAppDetails);
    const client = (yield call(doCreateUbiquityV2Client)).messages(appToken);
    const [, currentMessage] = yield select(selectContentMessage(updatedMessage.id));

    const ubiquityMessage = yield call(client.edit, currentMessage, { name: updatedMessage.name });

    const version = yield call(client.createVersion, ubiquityMessage, {
      body: updatedMessage.body,
      metadata: updatedMessage.metadata,
      payload: updatedMessage.payload,
      title: updatedMessage.name,
    });

    yield call(client.packageVersion, version, ubiquityMessage);

    const editedMessage = mapRawContentMessageToContentMessage({
      ...ubiquityMessage,
      latest_version: version,
      is_available: updatedMessage.published || false,
    });

    if (updatedMessage.published === true && currentMessage.published === false) {
      yield call(client.makeAvailable, ubiquityMessage);
    } else if (updatedMessage.published === false && currentMessage.published === true) {
      yield call(client.makeUnavailable, ubiquityMessage);
    }

    yield call(
      message.success,
      i18n.t(`${messageTranslationKey}:EditContentMessageWizard.successMessage`),
    );
    trackDataEvent('Content Message Updated', {
      messageId: updatedMessage.id,
      messageTitle: updatedMessage.name,
      messageType: type,
    });
    yield put(updateContentMessageSuccess(editedMessage));

    const history = yield getContext('history');
    yield call(history.goBack);
  } catch (err) {
    console.error(err);
    yield call(
      message.warning,
      i18n.t(`${messageTranslationKey}:EditContentMessageWizard.failedMessage`),
    );
    yield put(updateContentMessageFailed());
  }
}

function* doDeleteContentMessages({
  payload: { messageIds, messageTranslationKey },
}: IDeleteContentMessages): any {
  try {
    const { appToken } = yield select(selectAppDetails);
    const client = (yield call(doCreateUbiquityV2Client)).messages(appToken);

    for (const id of messageIds) {
      const [, message] = yield select(selectContentMessage(id));

      yield call(client.delete, message);
    }

    yield put(deleteContentMessagesSuccess(messageIds));

    yield call(
      message.success,
      i18n.t(`${messageTranslationKey}:DeleteMessage.deleteSuccess`, { count: messageIds.length }),
    );
  } catch (err) {
    console.error(err);
    yield call(
      message.warning,
      i18n.t(`${messageTranslationKey}:DeleteMessage.deleteFailed`, { count: messageIds.length }),
    );
    yield put(deleteContentMessagesFailed());
  }
}

function* doPublishContentMessage({
  payload: { id, translationKey },
}: IPublishContentMessage): any {
  try {
    const { appToken } = yield select(selectAppDetails);
    const client = (yield call(doCreateUbiquityV2Client)).messages(appToken);
    const [, contentMessage] = yield select(selectContentMessage(id));

    yield call(client.makeAvailable, contentMessage);

    yield put(publishContentMessageSuccess(id));
  } catch (err) {
    console.error(err);
    yield put(publishContentMessageFailed());
  }
}

function* doUnpublishContentMessage({
  payload: { id, translationKey },
}: IUnpublishContentMessage): any {
  try {
    const { appToken } = yield select(selectAppDetails);
    const client = (yield call(doCreateUbiquityV2Client)).messages(appToken);
    const [, contentMessage] = yield select(selectContentMessage(id));

    yield call(client.makeUnavailable, contentMessage);

    yield put(unpublishContentMessageSuccess(id));
  } catch (err) {
    console.error(err);
    yield put(unpublishContentMessageFailed());
  }
}
